import { Baby } from 'lucide-react';
import { type BabiesContent, type BabyMilestone, type FamilyContent, type KinLabel } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Switch } from '@london-life/ui/components/switch';
import { Field, NumberField, needSpec, type NumberSpec } from './form';

type BabyNumbers =
  | 'maxChildren'
  | 'arriveHours'
  | 'feedPence'
  | 'fullHours'
  | 'hungryBelow'
  | 'playMinutes'
  | 'renameDays'
  | 'renameAnswerHours';
const RULES: NumberSpec<BabyNumbers>[] = [
  { key: 'maxChildren', label: 'Children each player can have', unit: 'number', min: 1, max: 10, whole: true },
  {
    key: 'arriveHours',
    label: 'A baby arrives this many hours after the yes',
    unit: 'number',
    min: 0,
    max: 720,
    whole: true,
  },
  { key: 'feedPence', label: 'A feed', unit: 'pounds', min: 0, max: 10_000_000 },
  { key: 'fullHours', label: 'A feed keeps a baby full this many hours', unit: 'number', min: 1, max: 72, whole: true },
  {
    key: 'hungryBelow',
    label: 'Hungry again below this care (1 to 100)',
    unit: 'number',
    min: 1,
    max: 100,
    whole: true,
  },
  { key: 'playMinutes', label: 'Play once every this many minutes', unit: 'number', min: 1, max: 1_440, whole: true },
  { key: 'renameDays', label: 'A new name once every this many days', unit: 'number', min: 0, max: 365, whole: true },
  {
    key: 'renameAnswerHours',
    label: 'The other parent has this many hours to say no to a new name',
    unit: 'number',
    min: 1,
    max: 168,
    whole: true,
  },
];
const DAY: NumberSpec<'day'> = { key: 'day', label: 'From day', unit: 'number', min: 0, max: 3_650, whole: true };

// Family's children and relatives (as Lagos Life's babies and kin): the rules for babies, what feeding and
// playing do for the parent, the milestones, and what each kind of relative is called. Part of the content
// draft, published with it.
export function FamilyChildrenSections({
  family,
  change,
  readOnly,
}: {
  family: FamilyContent;
  change: (fn: (draft: FamilyContent) => void) => void;
  readOnly: boolean;
}) {
  const babies = family.babies;
  const edit = (fn: (draft: BabiesContent) => void) => change((draft) => fn(draft.babies));
  const milestone = (index: number, fn: (entry: BabyMilestone) => void) =>
    edit((draft) => {
      const entry = draft.milestones[index];
      if (entry) fn(entry);
    });
  const kin = (kind: KinLabel['kind'], fn: (entry: KinLabel) => void) =>
    change((draft) => {
      const entry = draft.kin.find((item) => item.kind === kind);
      if (entry) fn(entry);
    });
  return (
    <>
      <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Babies">
        <h2 className="content-subtitle">
          <Baby size={16} aria-hidden="true" /> Babies
        </h2>
        <label className="designer-check">
          <Switch
            checked={babies.visible}
            aria-label="Babies open"
            onCheckedChange={(on) => edit((draft) => void (draft.visible = on))}
          />{' '}
          Partners can start a family or adopt together
        </label>
        <div className="content-grid">
          {RULES.map((spec) => (
            <NumberField
              key={spec.key}
              id={`babies-${spec.key}`}
              spec={spec}
              value={babies[spec.key]}
              onChange={(value) => edit((draft) => void (draft[spec.key] = value))}
            />
          ))}
          {(['fun', 'social'] as const).map((key) => (
            <NumberField
              key={`feed-${key}`}
              id={`babies-feed-${key}`}
              spec={needSpec(key, `A feed: ${key} for the parent`)}
              value={babies.feedEffects[key] ?? 0}
              onChange={(value) => edit((draft) => void (draft.feedEffects[key] = value))}
            />
          ))}
          {(['fun', 'social'] as const).map((key) => (
            <NumberField
              key={`play-${key}`}
              id={`babies-play-${key}`}
              spec={needSpec(key, `Play: ${key} for the parent`)}
              value={babies.playEffects[key] ?? 0}
              onChange={(value) => edit((draft) => void (draft.playEffects[key] = value))}
            />
          ))}
        </div>
        <h3 className="content-mini-title">Milestones</h3>
        {babies.milestones.map((item, index) => (
          <div className="content-grid content-row" key={index} data-testid={`milestone-${index}`}>
            <Field id={`milestone-${index}-label`} label="Milestone">
              <Input
                id={`milestone-${index}-label`}
                value={item.label}
                maxLength={60}
                onChange={(e) => milestone(index, (entry) => void (entry.label = e.target.value))}
              />
            </Field>
            <Field id={`milestone-${index}-emoji`} label="Emoji">
              <Input
                id={`milestone-${index}-emoji`}
                value={item.emoji}
                maxLength={16}
                onChange={(e) => milestone(index, (entry) => void (entry.emoji = e.target.value))}
              />
            </Field>
            <NumberField
              id={`milestone-${index}-day`}
              spec={DAY}
              value={item.day}
              onChange={(value) => milestone(index, (entry) => void (entry.day = value))}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          disabled={babies.milestones.length >= 20}
          onClick={() =>
            edit((draft) =>
              draft.milestones.push({
                day: (draft.milestones.at(-1)?.day ?? 0) + 30,
                label: 'A new milestone',
                emoji: '⭐',
              }),
            )
          }
        >
          Add a milestone
        </Button>
      </fieldset>

      <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Relatives">
        <h2 className="content-subtitle">Relatives</h2>
        <p className="admin-muted">
          What players can ask each other to be in People. Each kind has its other side (an uncle&apos;s nephew), so
          kinds are named and switched off here, not added.
        </p>
        {family.kin.map((item) => (
          <div className="content-grid content-row" key={item.kind} data-testid={`kin-${item.kind}`}>
            <Field id={`kin-${item.kind}-label`} label="Called">
              <Input
                id={`kin-${item.kind}-label`}
                value={item.label}
                maxLength={40}
                onChange={(e) => kin(item.kind, (entry) => void (entry.label = e.target.value))}
              />
            </Field>
            <Field id={`kin-${item.kind}-emoji`} label="Emoji">
              <Input
                id={`kin-${item.kind}-emoji`}
                value={item.emoji}
                maxLength={16}
                onChange={(e) => kin(item.kind, (entry) => void (entry.emoji = e.target.value))}
              />
            </Field>
            <label className="designer-check">
              <Switch
                checked={item.visible}
                aria-label={`${item.label} on`}
                onCheckedChange={(on) => kin(item.kind, (entry) => void (entry.visible = on))}
              />{' '}
              On
            </label>
          </div>
        ))}
      </fieldset>
    </>
  );
}
